import { api } from '@/lib/api/client';
import type {
  Role, AdminUser, UserBranchesResponse, Specialty, AilmentTemplate,
  AIAssistantProfileSummary, AIAssistantProfile, MergeRequest, Branch,
  Ward, Doctor, QueueSettings, DoctorAvailabilityDay, DoctorBreak,
  AvailabilityException, ReportUnavailabilityResult, Bed, BillingSettings,
  LoyaltySettings, LoyaltySettingsInput, LoyaltyMembersResponse, LoyaltyLedgerRow,
  DrugReviewProposal, FormularyEntry, UnconfiguredDrug,
  DrugMasterRow, NewDrugMasterInput, Payer, BranchClaimProfile, ServiceMasterItem, PayerDocumentRule,
  PayerTariffsSettings, PayerTariff, Package, PackageItem, PackageBulkImportResult,
} from './types';
import { authFetch } from '../../api';

// Users & Roles — tenant-wide (no branch scoping; see UsersManagement.jsx).
export const getAllUsers = () => api.get<AdminUser[]>('/users');
export const createUser = (data: Partial<AdminUser> & { username: string; password: string }) => api.post<AdminUser>('/users', data);
export const updateUser = (id: number, data: Partial<AdminUser> & { password?: string }) => api.patch<AdminUser>(`/users/${id}`, data);
export const deactivateUser = (id: number) => api.delete<void>(`/users/${id}`);
export const getUserBranches = (id: number) => api.get<UserBranchesResponse>(`/users/${id}/branches`);
export const setUserBranches = (id: number, data: { branch_ids: number[]; default_branch_id: number }) => api.put<void>(`/users/${id}/branches`, data);

export const getAllRoles = () => api.get<Role[]>('/roles');
export const createRole = (data: { name: string; modules: string[] }) => api.post<Role>('/roles', data);
export const updateRole = (id: number, data: { modules?: string[]; mfa_required?: boolean }) => api.patch<Role>(`/roles/${id}`, data);
export const deleteRole = (id: number) => api.delete<void>(`/roles/${id}`);

// Specialties
export const getSpecialties = (all?: boolean) => api.get<Specialty[]>(`/specialties${all ? '?all=1' : ''}`);
export const createSpecialty = (data: { name: string }) => api.post<Specialty>('/specialties', data);
export const updateSpecialty = (id: number, data: Partial<Specialty>) => api.patch<Specialty>(`/specialties/${id}`, data);
export const deactivateSpecialty = (id: number) => api.delete<void>(`/specialties/${id}`);

// Ailment Templates (Assessment Modules) — maker-checker via Executive Director.
export const getAilmentTemplates = () => api.get<AilmentTemplate[]>('/ailment-templates');
export const createAilmentTemplate = (data: Partial<AilmentTemplate>) => api.post<AilmentTemplate>('/ailment-templates', data);
export const updateAilmentTemplate = (id: number, data: Partial<AilmentTemplate>) => api.patch<AilmentTemplate>(`/ailment-templates/${id}`, data);
export const deactivateAilmentTemplate = (id: number) => api.delete<void>(`/ailment-templates/${id}`);
export const resetAilmentTemplateToDefault = (id: number) => api.post<AilmentTemplate>(`/ailment-templates/${id}/reset-to-default`);
export const approveAilmentTemplate = (id: number) => api.post<void>(`/ailment-templates/${id}/approve`);
export const rejectAilmentTemplate = (id: number, notes: string) => api.post<void>(`/ailment-templates/${id}/reject`, { notes });

// AI Assistant Role profiles — maker-checker via Executive Director.
export const getAIAssistantProfiles = () => api.get<AIAssistantProfileSummary[]>('/ai-assistant-profiles');
export const getAIAssistantProfile = (id: number) => api.get<AIAssistantProfile>(`/ai-assistant-profiles/${id}`);
export const createAIAssistantProfile = (data: Partial<AIAssistantProfile>) => api.post<AIAssistantProfile>('/ai-assistant-profiles', data);
export const updateAIAssistantProfile = (id: number, data: Partial<AIAssistantProfile>) => api.patch<AIAssistantProfile>(`/ai-assistant-profiles/${id}`, data);
export const requestActivateAIAssistantProfile = (id: number) => api.post<void>(`/ai-assistant-profiles/${id}/request-activate`);
export const approveAIAssistantProfile = (id: number) => api.post<void>(`/ai-assistant-profiles/${id}/approve`);
export const rejectAIAssistantProfile = (id: number, notes: string) => api.post<void>(`/ai-assistant-profiles/${id}/reject`, { notes });

// Patient merges
export const getMergeRequests = () => api.get<MergeRequest[]>('/patient-merge-requests');
export const approveMergeRequest = (id: number) => api.post<void>(`/patient-merge-requests/${id}/approve`);
export const rejectMergeRequest = (id: number, notes: string) => api.post<void>(`/patient-merge-requests/${id}/reject`, { notes });

// Branches — tenant-wide.
export const getBranches = () => api.get<Branch[]>('/branches');
export const createBranch = (data: { code: string; name: string; address?: string; phone?: string; gstin?: string }) => api.post<Branch>('/branches', data);
export const updateBranch = (id: number, data: Partial<Branch>) => api.patch<Branch>(`/branches/${id}`, data);

// Wards & Beds
export const getWards = () => api.get<Ward[]>('/ip/wards');
export const createWard = (data: { name: string; ward_type: string; daily_rate: number; floor?: string; bed_count: number }) => api.post<Ward>('/ip/wards', data);
export const updateWard = (id: number, data: Partial<Ward>) => api.patch<Ward>(`/ip/wards/${id}`, data);
export const addBed = (wardId: number, data: { bed_number: string }) => api.post<Bed>(`/ip/wards/${wardId}/beds`, data);
export const updateBed = (id: number, data: Partial<Bed>) => api.patch<Bed>(`/ip/beds/${id}`, data);

// Doctor availability
export const getUsersByRole = (role: string) => api.get<Doctor[]>(`/users?role=${encodeURIComponent(role)}`);
export const getQueueSettings = () => api.get<QueueSettings>('/doctor-availability/settings');
export const updateQueueSettings = (data: QueueSettings) => api.patch<QueueSettings>('/doctor-availability/settings', data);
export const getDoctorAvailability = (params: { doctor_id: number | string; from?: string }) =>
  api.get<DoctorAvailabilityDay[]>(`/doctor-availability?${new URLSearchParams({ doctor_id: String(params.doctor_id), ...(params.from ? { from: params.from } : {}) }).toString()}`);
export const createDoctorAvailability = (data: { doctor_id: string | number; date: string; start_time: string; end_time: string; slot_duration_minutes: number }) =>
  api.post<DoctorAvailabilityDay>('/doctor-availability', data);
export const deleteDoctorAvailability = (id: number) => api.delete<void>(`/doctor-availability/${id}`);
export const addDoctorBreak = (availId: number, data: { start_time: string; end_time: string; label?: string }) => api.post<DoctorBreak>(`/doctor-availability/${availId}/breaks`, data);
export const deleteDoctorBreak = (availId: number, breakId: number) => api.delete<void>(`/doctor-availability/${availId}/breaks/${breakId}`);
export const getAvailabilityExceptions = (params: { doctor_id: number | string }) =>
  api.get<AvailabilityException[]>(`/doctor-availability/exceptions?${new URLSearchParams({ doctor_id: String(params.doctor_id) }).toString()}`);
export const reportUnavailability = (data: { doctor_id: string | number; date: string; exception_start: string; exception_end: string; reason: string }) =>
  api.post<ReportUnavailabilityResult>('/doctor-availability/exceptions', data);
export const cancelAppointmentSlot = (id: number, reason: string) => api.post<void>(`/appointment-slots/${id}/cancel`, { reason });

// Billing settings — centralized GST On/Off toggle + invoice numbering mode.
export const getBillingSettings = () => api.get<BillingSettings>('/billing-settings');
export const updateBillingSettings = (data: { gst_enabled?: boolean; gst_percentage_options?: number[] }) =>
  api.patch<BillingSettings>('/billing-settings', data);

// Pharmacy loyalty — the settings singleton behind Admin Panel → 💊 Pharmacy →
// 🎁 Loyalty Points.
// GET is open to anyone who can sell (the counter needs to know a point's
// value); PATCH is gated on admin:pharmacy-loyalty, deliberately separate
// from the counter's own grant so whoever runs the till cannot rewrite the
// earning rules. See routes/loyalty.js.
export const getLoyaltySettings = () => api.get<LoyaltySettings>('/loyalty/settings');
export const updateLoyaltySettings = (data: LoyaltySettingsInput) =>
  api.patch<LoyaltySettings>('/loyalty/settings', data);

export const getLoyaltyMembers = (q?: string) =>
  api.get<LoyaltyMembersResponse>('/loyalty/members', { query: { q } });

export const getLoyaltyLedger = (patientId: number) =>
  api.get<LoyaltyLedgerRow[]>(`/loyalty/history/${patientId}`);

/** A goodwill grant or a correction. Always attributed, always reasoned. */
export const adjustLoyaltyPoints = (data: { patient_id: number; points: number; note: string }) =>
  api.post<{ points_balance: number }>('/loyalty/adjust', data);

// Drug Master normalization review — never writes drug_master/medicines.drug_id
// except via /approve, and only after this admin decision.
export const getDrugReviewQueue = (status: string = 'pending') =>
  api.get<DrugReviewProposal[]>(`/admin/drug-review?status=${status}`);
export const approveDrugReview = (id: number, edits?: Partial<{
  medicine_ids: number[]; canonical_name: string; generic_name: string;
  strength: string; dosage_form: string; route: string; release_form: string; medicine_type: string;
}>) => api.post<DrugReviewProposal & { drug_id: number }>(`/admin/drug-review/${id}/approve`, edits || {});
export const rejectDrugReview = (id: number, reviewer_notes: string) =>
  api.post<DrugReviewProposal>(`/admin/drug-review/${id}/reject`, { reviewer_notes });

// Drug Master direct view/edit (POC) — see routes/drug-master.js. Bypasses
// the normalization-review workflow above on purpose; doesn't replace it.
export const listDrugMaster = (params?: { q?: string; active?: boolean }) => {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.active !== undefined) qs.set('active', String(params.active));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get<DrugMasterRow[]>(`/admin/drug-master${suffix}`);
};
export const getDrugMaster = (id: number) => api.get<DrugMasterRow>(`/admin/drug-master/${id}`);
export const createDrugMaster = (input: NewDrugMasterInput) => api.post<DrugMasterRow>('/admin/drug-master', input);
export const updateDrugMaster = (id: number, patch: NewDrugMasterInput) =>
  api.patch<DrugMasterRow>(`/admin/drug-master/${id}`, patch);

// Hospital Formulary — tenant policy layered on drug_master/medicines.
export const getFormularyList = () => api.get<FormularyEntry[]>('/admin/formulary');
export const getFormularyEntry = (id: number) => api.get<FormularyEntry>(`/admin/formulary/${id}`);
export const getUnconfiguredDrugs = (q: string = '') =>
  api.get<UnconfiguredDrug[]>(`/admin/formulary/candidates/unconfigured?q=${encodeURIComponent(q)}`);
export type FormularySaveInput = Partial<{
  status: string; prescribing_restriction: string; default_product_id: number | null;
  allow_substitution: boolean; notes: string; active: boolean; review_reason: string;
  effective_from: string; effective_to: string;
  products: Array<{ product_id: number; status: string; priority: number; allow_substitution: boolean; active: boolean; notes?: string }>;
}>;
export const createFormularyEntry = (drug_id: number, data: FormularySaveInput) =>
  api.post<FormularyEntry>('/admin/formulary', { drug_id, ...data });
export const updateFormularyEntry = (id: number, data: FormularySaveInput) =>
  api.put<FormularyEntry>(`/admin/formulary/${id}`, data);
export const getProductsForDrug = (drugId: number) =>
  api.get<Array<{ id: number; generic_name: string; brand_name: string | null; strength: string | null; dosage_form: string | null }>>(`/medicines?drug_id=${drugId}`);

// "+ New brand" onboarding fast path (§22 of the Drug Master architecture
// doc) — creates a product with drug_id pre-set, so it's immediately
// resolved and skips the normalization review queue entirely. Reuses the
// same shared POST /api/medicines every product-creation path goes through
// (routes/medicines.js, §20) — never a second creation implementation.
export const createMedicineForDrug = (data: {
  drug_id: number; generic_name: string; brand_name?: string; strength?: string; dosage_form?: string;
  hsn_code?: string; gst_percent?: number; schedule?: string; manufacturer_name?: string;
}) => api.post<{ id: number; generic_name: string; brand_name: string | null; strength: string | null; dosage_form: string | null }>('/medicines', data);

// Insurance Claims — payers (insurer/TPA/scheme/corporate organisations) and
// the per-branch claim identity profile (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md
// §4.1/§4.2). GET on both is open to anyone who can capture coverage or work
// a claim; writes need admin:payers (routes/payers.js, routes/branch-claim-profile.js).
export const getPayers = (params?: { q?: string; type?: string; active?: 'all' | 'true' | 'false' }) => {
  const qs = new URLSearchParams(params as Record<string, string> ?? {}).toString();
  return api.get<Payer[]>(`/payers${qs ? `?${qs}` : ''}`);
};
export const createPayer = (data: Partial<Payer> & { payer_type: string; code: string; name: string }) => api.post<Payer>('/payers', data);
export const updatePayer = (id: number, data: Partial<Payer>) => api.patch<Payer>(`/payers/${id}`, data);
export const deletePayer = (id: number) => api.delete<void>(`/payers/${id}`);

export const getBranchClaimProfile = () => api.get<BranchClaimProfile>('/branch-claim-profile');
export const updateBranchClaimProfile = (data: Partial<BranchClaimProfile>) => api.patch<BranchClaimProfile>('/branch-claim-profile', data);

// The chargeable-service master (routes/service-master.js) — Phase 4.5.7.
// Same open-read/admin-write split as payers.
export const getServiceMaster = (params?: { q?: string; claim_head?: string; active?: 'all' | 'true' | 'false' }) => {
  const qs = new URLSearchParams(params as Record<string, string> ?? {}).toString();
  return api.get<ServiceMasterItem[]>(`/service-master${qs ? `?${qs}` : ''}`);
};
export const createServiceMaster = (data: Partial<ServiceMasterItem> & { code: string; name: string; claim_head: string }) =>
  api.post<ServiceMasterItem>('/service-master', data);
export const updateServiceMaster = (id: number, data: Partial<ServiceMasterItem>) => api.patch<ServiceMasterItem>(`/service-master/${id}`, data);
export const deleteServiceMaster = (id: number) => api.delete<void>(`/service-master/${id}`);

// Document checklist rules (routes/payer-document-rules.js) — Insurance
// Claims Phase 5.4, wireframe S9's 4th tab. Same open-read/admin-write split.
export const getPayerDocumentRules = (params?: { profile?: string; stage?: string }) => {
  const qs = new URLSearchParams(params as Record<string, string> ?? {}).toString();
  return api.get<PayerDocumentRule[]>(`/payer-document-rules${qs ? `?${qs}` : ''}`);
};
export const getPayerDocumentRuleProfiles = () => api.get<Array<{ profile: string; stage: string }>>('/payer-document-rules/profiles');
export const createPayerDocumentRule = (data: Partial<PayerDocumentRule> & { profile: string; stage: string; doc_key: string; label: string }) =>
  api.post<PayerDocumentRule>('/payer-document-rules', data);
export const updatePayerDocumentRule = (id: number, data: Partial<PayerDocumentRule>) => api.patch<PayerDocumentRule>(`/payer-document-rules/${id}`, data);
export const deletePayerDocumentRule = (id: number) => api.delete<void>(`/payer-document-rules/${id}`);

// Payer tariffs & packages (routes/payer-tariffs.js, routes/packages.js) —
// Insurance Claims Phase 6.1, wireframe S9's 3rd tab. Behind the
// claims.payer_tariffs flag; getPayerTariffsSettings()/updatePayerTariffsSettings()
// is where an Admin turns it on. Same open-read/admin-write split
// (admin:claim-tariffs) as the other Insurance Claims sub-tabs.
export const getPayerTariffsSettings = () => api.get<PayerTariffsSettings>('/payer-tariffs/settings');
export const updatePayerTariffsSettings = (data: Partial<PayerTariffsSettings>) =>
  api.patch<PayerTariffsSettings>('/payer-tariffs/settings', data);

export const getPayerTariffs = (params?: { payer_id?: string | number; service_id?: string | number }) => {
  const qs = new URLSearchParams(params as Record<string, string> ?? {}).toString();
  return api.get<PayerTariff[]>(`/payer-tariffs${qs ? `?${qs}` : ''}`);
};
export const createPayerTariff = (data: Partial<PayerTariff> & { service_id: number; rate: number }) =>
  api.post<PayerTariff>('/payer-tariffs', data);
export const updatePayerTariff = (id: number, data: Partial<PayerTariff>) => api.patch<PayerTariff>(`/payer-tariffs/${id}`, data);
export const deletePayerTariff = (id: number) => api.delete<void>(`/payer-tariffs/${id}`);
export const resolvePayerTariffRate = (params: { service_id: number; payer_id?: number; room_category?: string; admission_id?: number }) => {
  const qs = new URLSearchParams(params as unknown as Record<string, string>).toString();
  return api.get<{ ok: boolean; source: string; rate: number; packageId?: number }>(`/payer-tariffs/resolve?${qs}`);
};

export const getPackages = (params?: { q?: string; payer_id?: string | number; active?: 'all' | 'true' | 'false' }) => {
  const qs = new URLSearchParams(params as Record<string, string> ?? {}).toString();
  return api.get<Package[]>(`/packages${qs ? `?${qs}` : ''}`);
};
export const getPackage = (id: number) => api.get<Package & { items: PackageItem[] }>(`/packages/${id}`);
export const createPackage = (data: Partial<Package> & { code: string; name: string; rate: number }) => api.post<Package>('/packages', data);
export const updatePackage = (id: number, data: Partial<Package>) => api.patch<Package>(`/packages/${id}`, data);
export const deletePackage = (id: number) => api.delete<void>(`/packages/${id}`);
export const replacePackageItems = (id: number, items: Array<{ service_id: number; qty_included?: number }>) =>
  api.put<PackageItem[]>(`/packages/${id}/items`, { items });

export const bulkImportPackages = async (file: File): Promise<PackageBulkImportResult> => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await authFetch('/api/packages/bulk-import', { method: 'POST', body: fd });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Import failed');
  return data as PackageBulkImportResult;
};

// A plain <a href="/api/packages/import-template"> would navigate with no
// Authorization header (this app is token-auth, not cookie-auth — see
// features/billing/api.ts#openBillPdf for the precedent this follows) and
// 401. Fetches the CSV as an authenticated blob and triggers the save via
// a detached, auto-clicked <a download>.
export const downloadPackageCsvTemplate = async (): Promise<void> => {
  const res = await authFetch('/api/packages/import-template');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = 'package_import_template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
};

// ── Payers / Service master CSV import & export (Insurance Claims plan
// 1.2/2.1, wireframe S9). Create-only on the server: an existing or
// repeated code comes back as a row-level error (utils/masterCsvImport.js).

export interface MasterCsvImportRow {
  row: number;
  code: string | null;
  status: 'created' | 'error';
  message?: string;
  id?: number;
}
export interface MasterCsvImportResult {
  processed: number;
  created: number;
  failed: number;
  results: MasterCsvImportRow[];
}
export type ClaimsMaster = 'payers' | 'service-master';

export const bulkImportMaster = async (master: ClaimsMaster, file: File): Promise<MasterCsvImportResult> => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await authFetch(`/api/${master}/bulk-import`, { method: 'POST', body: fd });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Import failed');
  return data as MasterCsvImportResult;
};

/** Authenticated CSV download (template or full export) — same blob +
 * detached <a download> approach as downloadPackageCsvTemplate above. */
export const downloadMasterCsv = async (master: ClaimsMaster, kind: 'import-template' | 'export'): Promise<void> => {
  const res = await authFetch(`/api/${master}/${kind}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `${master.replace('-', '_')}${kind === 'import-template' ? '_import_template' : ''}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
};

// ── Branch hospital seal image (design §6.2 F, wireframe S9) — a branch-level
// file, not a patient document. Printed only on forms generated by a
// claims:sign-forms holder.
export const uploadBranchSeal = async (file: File): Promise<BranchClaimProfile> => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await authFetch('/api/branch-claim-profile/seal', { method: 'POST', body: fd });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Upload failed');
  return data as BranchClaimProfile;
};
export const removeBranchSeal = () => api.delete<BranchClaimProfile>('/branch-claim-profile/seal');
/** The seal as an object URL for preview (token-auth, so not a plain <img src>). Null when none is set. */
export const fetchBranchSealUrl = async (): Promise<string | null> => {
  const res = await authFetch('/api/branch-claim-profile/seal');
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return URL.createObjectURL(await res.blob());
};
